<template>
  <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
    <div class="modal-content">
      <button @click="closeModal" class="close-btn" aria-label="Close modal" type="button">&times;</button>

      <div class="modal-header">
        <h2 id="modal-title">Sign In</h2>
        <p class="modal-subtitle">Log in or create an account to start your search</p>
      </div>

      <form @submit.prevent="handleLogin" class="login-form" v-if="!showOtpStep">

        <Alert v-if="infoMessage" variant="default" class="login-alert">
          <AlertDescription>{{ infoMessage }}</AlertDescription>
        </Alert>

        <Alert v-if="errors.general" variant="destructive" class="login-alert">
          <AlertDescription>{{ errors.general }}</AlertDescription>
        </Alert>

        <!-- OAuth Login Buttons -->
        <div class="oauth-section">
          <Button
            type="button"
            class="oauth-button oauth-google"
            @click="handleOAuthLogin('google')"
            :disabled="loading"
          >
            <svg class="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>

          <!-- <Button
            type="button"
            class="oauth-button oauth-microsoft"
            @click="handleOAuthLogin('microsoft')"
            :disabled="loading"
          >
            <svg class="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#F25022" d="M1 1h10v10H1z"/>
              <path fill="#7FBA00" d="M13 1h10v10H13z"/>
              <path fill="#00A4EF" d="M1 13h10v10H1z"/>
              <path fill="#FFB900" d="M13 13h10v10H13z"/>
            </svg>
            Sign in with Microsoft
          </Button>

          <Button
            type="button"
            class="oauth-button oauth-facebook"
            @click="handleOAuthLogin('facebook')"
            :disabled="loading"
          >
            <svg class="oauth-icon" viewBox="0 0 24 24" width="20" height="20" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Sign in with Facebook
          </Button> -->
        </div>

        <div class="divider">
          <span>or</span>
        </div>

        <div class="form-group">
          <Label for="email">Email Address</Label>
          <Input
            id="email"
            v-model="form.email"
            type="email"
            required
            placeholder="E-mail"
            :class="{ 'input-error': errors.email }"
            aria-describedby="email-error"
            :aria-invalid="!!errors.email"
          />
          <span v-if="errors.email" id="email-error" class="error-message" role="alert">{{ errors.email }}</span>
        </div>

        <div class="form-group">
          <Label for="password">Password</Label>
          <Input
            id="password"
            v-model="form.password"
            type="password"
            required
            placeholder="Password"
            :class="{ 'input-error': errors.password }"
            aria-describedby="password-error"
            :aria-invalid="!!errors.password"
          />
          <span v-if="errors.password" id="password-error" class="error-message" role="alert">{{ errors.password }}</span>
        </div>

        <div class="forgot-password-link">
          <a href="#" @click.prevent="handleForgotPassword">Forgot Password?</a>
        </div>

        <Button
          type="submit"
          class="login-submit-btn"
          :disabled="loading"
          :aria-busy="loading"
          aria-label="Sign in to your account"
        >
          <span v-if="loading">Signing In...</span>
          <span v-else>Sign In</span>
        </Button>

        <div class="form-footer">
          <p>Doesn't have an account?
            <a href="#" @click.prevent="switchToRegister">Create Account</a>
          </p>
        </div>
      </form>

      <!-- OTP Verification Step -->
      <form @submit.prevent="handleOtpVerify" class="login-form" v-if="showOtpStep">
        <Alert v-if="errors.general" variant="destructive" class="login-alert">
          <AlertDescription>{{ errors.general }}</AlertDescription>
        </Alert>

        <Alert variant="default" class="login-alert otp-info-alert">
          <AlertDescription>
            We've sent a 6-digit verification code to <strong>{{ form.email }}</strong>
          </AlertDescription>
        </Alert>

        <div class="form-group">
          <Label for="otp-code">Verification Code</Label>
          <Input
            id="otp-code"
            v-model="otpForm.code"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="6"
            required
            placeholder=""
            :class="{ 'input-error': errors.otp }"
            aria-describedby="otp-error"
            :aria-invalid="!!errors.otp"
            autocomplete="one-time-code"
          />
          <span v-if="errors.otp" id="otp-error" class="error-message" role="alert">{{ errors.otp }}</span>
        </div>

        <div class="otp-actions">
          <Button
            type="submit"
            class="login-submit-btn"
            :disabled="loading || otpForm.code.length !== 6"
            :aria-busy="loading"
          >
            <span v-if="loading">Verifying...</span>
            <span v-else>Verify Code</span>
          </Button>

          <div class="resend-section">
            <Button
              type="button"
              variant="ghost"
              class="resend-btn"
              @click="handleResendOtp"
              :disabled="loading || resendCooldown > 0"
            >
              <span v-if="resendCooldown > 0">Resend code in {{ resendCooldown }}s</span>
              <span v-else>Resend Code</span>
            </Button>
          </div>

          <Button
            type="button"
            variant="ghost"
            class="back-btn"
            @click="backToLogin"
            :disabled="loading"
          >
            ← Back to Login
          </Button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import api from '@/api'
import { API_ENDPOINTS, HTTP_STATUS, ERROR_MESSAGES } from '@/constants'
import { initiateOAuth } from '@/utils/oauth'

export default {
  name: 'LoginModal',
  components: {
    Button,
    Input,
    Label,
    Alert,
    AlertDescription
  },
  props: {
    prefillEmail: {
      type: String,
      default: ''
    },
    infoMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      form: {
        email: this.prefillEmail || '',
        password: ''
      },
      otpForm: {
        code: ''
      },
      errors: {},
      loading: false,
      showOtpStep: false,
      resendCooldown: 0,
      resendTimer: null
    }
  },
  methods: {
    async handleLogin() {
      this.errors = {}
      this.loading = true

      try {
        const response = await api.post(API_ENDPOINTS.LOGIN, {
          email: this.form.email,
          password: this.form.password
        })

        // Check if OTP is required
        if (response.data.requires_otp) {
          this.showOtpStep = true
          this.startResendCooldown()
        } else {
          // Legacy: Handle direct token response (shouldn't happen with new flow)
          const token = response.data.access_token

          const userResponse = await api.get(API_ENDPOINTS.ME, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })

          this.$store.commit('auth/setAuth', {
            token,
            user: userResponse.data
          })

          this.$emit('login-success')
        }

      } catch (error) {
        console.error('Login error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        })

        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.errors.general = 'Invalid email or password'
        } else if (error.response?.data?.detail) {
          this.errors.general = error.response.data.detail
        } else if (error.message) {
          this.errors.general = `Login failed: ${error.message}`
        } else {
          this.errors.general = ERROR_MESSAGES.GENERIC_ERROR
        }
      } finally {
        this.loading = false
      }
    },

    async handleOtpVerify() {
      this.errors = {}
      this.loading = true

      try {
        const response = await api.post(API_ENDPOINTS.LOGIN_VERIFY_OTP, {
          email: this.form.email,
          otp_code: this.otpForm.code
        })

        const token = response.data.access_token

        const userResponse = await api.get(API_ENDPOINTS.ME, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        this.$store.commit('auth/setAuth', {
          token,
          user: userResponse.data
        })

        this.$emit('login-success')

      } catch (error) {
        console.error('OTP verification error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        })

        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.errors.general = 'Invalid or expired verification code. Please try again.'
        } else if (error.response?.data?.detail) {
          this.errors.general = error.response.data.detail
        } else {
          this.errors.general = 'Verification failed. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    async handleResendOtp() {
      this.errors = {}
      this.loading = true

      try {
        await api.post(API_ENDPOINTS.LOGIN, {
          email: this.form.email,
          password: this.form.password
        })

        this.otpForm.code = ''
        this.startResendCooldown()
        
        // Show success message briefly
        this.errors.general = ''
        
      } catch (error) {
        console.error('Resend OTP error:', error)
        this.errors.general = 'Failed to resend code. Please try again.'
      } finally {
        this.loading = false
      }
    },

    startResendCooldown() {
      this.resendCooldown = 60
      if (this.resendTimer) {
        clearInterval(this.resendTimer)
      }
      this.resendTimer = setInterval(() => {
        this.resendCooldown--
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      }, 1000)
    },

    backToLogin() {
      this.showOtpStep = false
      this.otpForm.code = ''
      this.errors = {}
      if (this.resendTimer) {
        clearInterval(this.resendTimer)
        this.resendTimer = null
      }
      this.resendCooldown = 0
    },

    closeModal() {
      this.backToLogin()
      this.$emit('close')
    },
    switchToRegister() {
      this.$emit('close')
      this.$emit('show-register')
    },
    handleForgotPassword() {
      this.$emit('close')
      this.$emit('show-forgot-password')
    },
    handleOAuthLogin(provider) {
      try {
        initiateOAuth(provider)
      } catch (error) {
        this.errors.general = 'Failed to initiate OAuth sign in. Please try again.'
      }
    }
  },

  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle, rgba(255, 255, 255, 0.04) 1px, transparent 1.4px) 0 0 / 14px 14px,
    radial-gradient(ellipse at top left, rgba(255, 255, 255, 0.05), transparent 70%),
    #10372f;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}

.modal-content {
  position: relative;
  background: rgba(10, 45, 36, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  width: 100%;
  max-width: 460px;
  max-height: 92vh;
  overflow-y: auto;
  color: #faf9f7;
  padding: 2.5rem 2.25rem;
}

.modal-header {
  text-align: center;
  margin-bottom: 1.75rem;
}

.modal-header h2 {
  margin: 0 0 0.5rem;
  font-size: 1.625rem;
  font-weight: 600;
  color: #faf9f7;
  letter-spacing: 0.2px;
}

.modal-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.95rem;
  font-weight: 400;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Labels — visually hidden but accessible */
.form-group :deep(label) {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Inputs — transparent over dark green */
.form-group :deep(input) {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #ffffff;
  border-radius: 9999px;
  padding: 0.85rem 1.25rem;
  height: auto;
  font-size: 0.95rem;
}

.form-group :deep(input::placeholder) {
  color: rgba(255, 255, 255, 0.55);
}

.form-group :deep(input:focus),
.form-group :deep(input:focus-visible) {
  outline: none;
  border-color: #ffffff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.15);
}

.input-error {
  border-color: #fca5a5 !important;
}

.error-message {
  color: #fca5a5;
  font-size: 0.8125rem;
  margin-top: 0.125rem;
  display: block;
  padding-left: 1.25rem;
}

/* Submit — cream pill */
.login-submit-btn {
  width: 100%;
  background: #faf9f7;
  color: #0a1d14;
  border-radius: 9999px;
  padding: 0.95rem 1.25rem;
  height: auto;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  margin-top: 0.5rem;
}

.login-submit-btn:hover:not(:disabled) {
  background: #ffffff;
  color: #0a1d14;
}

.login-alert {
  margin-bottom: 0.5rem;
  background: rgba(252, 165, 165, 0.12);
  border-color: rgba(252, 165, 165, 0.4);
  color: #fecaca;
}

.form-footer {
  text-align: center;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.78);
}

.form-footer p {
  margin: 0;
}

.form-footer a,
.forgot-password-link a {
  color: #ffffff;
  text-decoration: none;
  font-weight: 600;
}

.form-footer a:hover,
.forgot-password-link a:hover {
  text-decoration: underline;
}

.forgot-password-link {
  text-align: left;
  margin-top: -0.25rem;
  margin-bottom: 0.25rem;
}

.forgot-password-link a {
  font-size: 0.875rem;
  font-weight: 500;
}

/* OAuth */
.oauth-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.oauth-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  height: auto;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: #ffffff;
  font-weight: 500;
  font-size: 0.95rem;
  border-radius: 9999px;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.oauth-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.6);
}

.oauth-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.oauth-icon {
  flex-shrink: 0;
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 0.75rem 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
}

.divider span {
  padding: 0 0.75rem;
}

/* OTP */
.otp-info-alert {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
}

.otp-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.resend-section {
  text-align: center;
}

.resend-btn {
  color: #ffffff;
  font-size: 0.875rem;
  padding: 0.5rem;
  background: transparent;
}

.resend-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.06);
  color: #ffffff;
}

.resend-btn:disabled {
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}

.back-btn {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  background: transparent;
}

.back-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.06);
  color: #ffffff;
}

#otp-code {
  font-size: 1.25rem;
  letter-spacing: 0.5rem;
  text-align: center;
  font-weight: 600;
}

/* Mobile */
@media (max-width: 480px) {
  .modal-content {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }

  .modal-header h2 {
    font-size: 1.4rem;
  }

  .modal-subtitle {
    font-size: 0.875rem;
  }
}
</style>

